import { Component, OnInit,ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, map } from 'rxjs';
import { TabViewModule } from 'primeng/tabview';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { ConfirmationService, ConfirmEventType, MessageService, MenuItem } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { AutoComplete } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { SplitButtonModule } from 'primeng/splitbutton';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ChartModule } from 'primeng/chart';
import { ExportMovinService } from '../../../services/export-movin.service';

import { TabViewChangeEvent } from 'primeng/tabview';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { WatchComponent } from '../watch/watch.component';
import { buildNotificationTag, NotificationDialogComponent, NotificationSummaryChange, NotificationTagInfo } from '../notification-dialog/notification-dialog.component';
import { NotificationDialogService } from '../notification-dialog/notification-dialog.service';
import { MergenDialogComponent } from '../merge-dialog/merge-dialog.component';
import { ViewChild } from '@angular/core';

import { MoveinRecord } from '../../../models/movein-record.model';
import { DocumentGroupsGradesModel, DocumentStatusModel, FileFeatureGradeModel } from '../../../models/docoment-status.model';

import { Pagination } from '../../../services/pagination.abstruct';
import { TranslateService } from '../../../services/translate.service';
import { WatchService } from '../watch/watch.service';
import { ViewScopeService } from '../../../services/view-scope.service';

import { EyeDialogComponent } from '../eye-dialog/eye-dialog.component';
import { DirectionService } from '../../../services/direction.service';
import { DocApprovalService, GlobalTableInfo } from './doc-approval.service';
import { RefreshService } from '../../../services/refresh.service';
import { WorkbenchIntegratorService } from '../../../services/workbench-integrator.service';

import { LastViewed } from '../../../services/workbench-integrator.service';


interface Document extends GlobalTableInfo {
	isAllowedToSend?: boolean;
	DateEnteredMonthYear?: string;
	DateEnteredGroupSort?: number;
	DateEnteredDay?: string;
	DateEnteredDaySort?: number;
	expanded?: boolean;
}



interface ExpenseTypeOption {
	label: string;
	value: string;
}

interface FileFeatureGradeViewModel extends FileFeatureGradeModel {
	ChartColorNotification?: string;
}

@Component({
	selector: 'app-doc-approval',	
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		DialogModule,
		TabViewModule,
		TagModule,
		ProgressBarModule,
		TableModule,
		ButtonModule,
		RippleModule,
		TooltipModule,
		ConfirmDialogModule,
		ToastModule,
		DropdownModule,
		OverlayPanelModule,
		InputTextModule,
		CheckboxModule,
		SplitButtonModule,
		ChartModule,
		WatchComponent,
		NotificationDialogComponent,
		MergenDialogComponent
	],
	providers: [ConfirmationService, MessageService, DialogService],
	templateUrl: './doc-approval.component.html',
	styleUrls: ['./doc-approval.component.scss']
})
export class DocApprovalComponent extends Pagination implements OnInit {

	// Main mode of file: History | Active = 1 | 0.
	public classMode: 0 | 1 = 0;

	// Workbench state
	workbenchFileRecIds: number[] = [];
	displayWorkbenchDialog: boolean = false;

	// Bulk-move ('truck') state: lets a person mark several rows and reassign them all at once,
	// fully separate from selectedDocuments/export checkbox semantics.
	bulkMoveModeActive: boolean = false;
	bulkMoveFileRecIds: number[] = [];

	// Journal-less files state (files with no real journal to send to movein)
	displayJournalLessDialog: boolean = false;

	@ViewChildren('watchRef') watchComponents!: QueryList<WatchComponent>;

	loading: boolean = true;
	errorMessage: string | null = null;

	selectedDocuments: Document[] = []; // For table row selection (multi-select)


	ref?: DynamicDialogRef;

	allDocuments: Document[] = [];
	manualConfirmationData: Document[] = [];
	groupedDocuments: Document[] = [];
	notToRegisterData: Document[] = [];
	expenseTypeOptions: ExpenseTypeOption[] = [];
	returnToRegisterOptions: ExpenseTypeOption[] = [];
	editModeId: number | null = null;
	selectedDocument: Document | null = null;


	direction: 'ltr' | 'rtl' = 'rtl';
	columnHeadersMap: { [key: string]: string } = {};

	// For Totals Summary smart filter
	totalSummaryFilter = { total: '', vat: '', vatPercent: '' };
	hasActiveTableFilters: boolean = false;

	displayedDocuments: Document[] = [];  // All the documents currently displayed (filtered)

	// New property for selected grouped documents
	selectedGroupedDocuments: Document[] = [];

	// New properties for dynamic tab movement
	tabHeaders = ['Manual Confirmation', 'Auto Confirmed', 'Not to Register'];
	currentTabIndex = 0;
	moveOptions: ExpenseTypeOption[] = [];

	// Lets the user pick which underlying value to sort/filter by on columns that display multiple values at once
	totalsSummaryFieldOptions: ExpenseTypeOption[] = [];
	totalsSummarySortField: string = 'total_with_tax';
	expenseFieldOptions: ExpenseTypeOption[] = [];
	expenseSortField: string = 'CustomerSupplierExpenseCodeName';
	categoryFieldOptions: ExpenseTypeOption[] = [];
	categorySortField: string = 'FileCategory';
	supplierFieldOptions: ExpenseTypeOption[] = [];
	supplierSortField: string = 'CustomerName';

	// Property to track expanded rows
	expandedRows: { [key: string]: boolean } = {};
	fileGroupGrades: { [fileRecId: number]: DocumentGroupsGradesModel[] } = {};
	fileFeatureGrades: { [fileRecId: number]: FileFeatureGradeViewModel[] } = {};
	fileGradesLoading: { [fileRecId: number]: boolean } = {};
	fileGradePolarAreaData: { [fileRecId: number]: any } = {};
	fileGradePolarAreaOptions: any = {
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'right',
				align: 'center',
				labels: {
					usePointStyle: true,
					pointStyle: 'circle',
					color: '#111827'
				}
			}
		},
		scales: {
			r: {
				min: 0,
				max: 5,
				grid: {
					circular: true,
					color: 'var(--surface-border)'
				},
				ticks: {
					stepSize: 1,
					color: '#6B7280',
					callback: (value: string | number) => Number(value) === 0 ? '' : value
				}
			}
		}
	};

	fileClassifications: DocumentStatusModel[] = [];
	activeClassificationIndex: number = 0;
	docApprovalStateKey: string = 'tab_doc_watch-session-10';
	isAttempt: 0 | 1 = 0;
	exportActions: MenuItem[] = [];
	protected selectedGradeLevel: number = 10;

	constructor(
		private confirmationService: ConfirmationService,
		private messageService: MessageService,
		protected docApprovalService: DocApprovalService,
		private refreshService: RefreshService,
		public directionServ: DirectionService,
		private dialogService: DialogService,
		public translateServ: TranslateService,
		public workbenchIntegrator: WorkbenchIntegratorService,
		public watchService: WatchService,
		public viewScopeService: ViewScopeService,
		private notificationDialogService: NotificationDialogService,
		private exportMovinService: ExportMovinService // <-- Injected
	) { super(); }


	ngOnInit(): void {
		this.initializeColumnHeadersMap();
		this.direction = this.directionServ.isRtl() ? 'rtl' : 'ltr';
		this.applyGradeChartThemeColors();
		this.exportActions = [
			{
				label: this.translateServ.dictionary['SIMULATION'],
				icon: 'pi pi-sliders-h',
				command: () => this.onExportButtonClick(1)
			}
		];
		this.totalsSummaryFieldOptions = [
			{ label: this.translateServ.dictionary['total'] || 'Total', value: 'total_with_tax' },
			{ label: this.translateServ.dictionary['tax'] || 'VAT', value: 'tax' },
			{ label: this.translateServ.dictionary['vat_in_precentages'] || 'VAT %', value: 'vat_in_precentages' }
		];
		this.expenseFieldOptions = [
			{ label: this.translateServ.dictionary['PREV_EXP_ACC_EXP'] || 'Expense', value: 'CustomerSupplierExpenseCodeName' },
			{ label: this.translateServ.dictionary['PREV_EXP_ACC_EXP_CODE'] || 'Movement Type', value: 'Sug_tnua' }
		];
		this.categoryFieldOptions = [
			{ label: this.translateServ.dictionary['FF_DOC'] || 'Document', value: 'FileCategory' },
			{ label: this.translateServ.dictionary['doc_num'] || 'Doc Number', value: 'DocNumber' }
		];
		this.supplierFieldOptions = [
			{ label: this.translateServ.dictionary['SUPPLIER'] || 'Supplier', value: 'CustomerName' },
			{ label: this.translateServ.dictionary['JMT_COL_BUSINESS_ID'] || 'Business ID', value: 'Business_ID' }
		];
		this.loadFileClassifications();

		// Restore last viewed document selection if available
		const lastViewed = this.workbenchIntegrator.getLastViewed() as LastViewed;
		if (lastViewed && typeof lastViewed.documentId === 'number') {
			setTimeout(() => {
				const doc = this.allDocuments.find(d => d.FileRecID === lastViewed.documentId);
				if (doc) {
					this.selectedDocuments = [doc];
					// Optionally scroll to or highlight the row
				}
			}, 500);
		}
	}


	initData(fullInit: boolean = false): void {
		this.loading = true;
		this.errorMessage = null;
		this.docApprovalStateKey = `${this.getTableStateKeyPrefix()}-${this.selectedGradeLevel}`;

		this.docApprovalService.loadAllFiles(this.selectedGradeLevel).subscribe({
			next: (data: GlobalTableInfo[]) => {
				console.warn('!!!! Loaded documents:', data);
				this.allDocuments = data.map(doc => this.prepareDocumentForDisplay({ ...doc, isAllowedToSend: false }));
				const groupSortKey = this.classMode === 1 ? 'DateEnteredGroupSort' : 'DateEnteredDaySort';
				this.allDocuments.sort((a, b) => (b[groupSortKey] ?? 0) - (a[groupSortKey] ?? 0));
				if (fullInit) {
					this.watchService.getInvoices(
						this.viewScopeService.getCurrentCustomer(),
						this.viewScopeService.getCurrentOpertaingUserId().toString(),
						-1
					).subscribe({
						next: (invoices: any[]) => {
							console.log('!!!! Fetched invoices:', invoices);
							const docJournalMap: { [FileRecID: number]: number[] } = {};
							invoices.forEach(inv => {
								const docId = inv.FileRecID;
								if (!docJournalMap[docId]) docJournalMap[docId] = [];
								if (inv.JournalId != null) docJournalMap[docId].push(inv.ID);
							});
							console.log('docJournalMap:', docJournalMap);

							const docToJournals: { [docId: number]: Set<number> } = {};
							invoices.forEach(inv => {
								const docId = inv.FileRecID;
								const journalId = inv.ID;
								if (docId == null || journalId == null) return;
								if (!docToJournals[docId]) docToJournals[docId] = new Set();
								docToJournals[docId].add(journalId);
							});
							Object.entries(docToJournals).forEach(([docId, journalSet]) => {
								this.workbenchIntegrator.ensureDocumentAndJournalsExist(Number(docId), Array.from(journalSet));
							});
							// Files with zero real journals get a pseudo journal so they can still be (de)selected
							this.allDocuments.forEach(doc => this.workbenchIntegrator.ensurePseudoJournalForJournalLessDocument(doc.FileRecID));
							console.log('DJWS after ensureDocumentAndJournalsExist:', localStorage.getItem(this.workbenchIntegrator.getStorageName()));

							const notifMap = new Map<number, { NotifCount: number, NotifColor: string | null }>();
							invoices.forEach(inv => {
								notifMap.set(inv.FileRecID, {
									NotifCount: inv.NotifCount ?? 0,
									NotifColor: inv.NotifColor ?? null
								});
							});
							this.allDocuments.forEach(doc => {
								const notif = notifMap.get(doc.FileRecID);
								if (notif) {
									(doc as any).NotifCount = notif.NotifCount;
									(doc as any).NotifColor = notif.NotifColor;
								} else {
									(doc as any).NotifCount = 0;
									(doc as any).NotifColor = null;
								}
							});
							this.applyUnresolvedNotificationSummaries(this.allDocuments);
							this.syncSelectedDocumentsFromDJWS();
							this.refreshData();
							const uniqueExpenseTypes = [...new Set(this.allDocuments.map(doc => doc.FileCategory))];
							this.expenseTypeOptions = uniqueExpenseTypes.map(type => ({
								label: type,
								value: type
							}));
							this.returnToRegisterOptions = this.expenseTypeOptions.filter(opt => opt.value !== 'Not to Register');
							this.loading = false;
						},
						error: (err) => {
							console.error('Error fetching data:', err);
							this.errorMessage = this.translateServ.dictionary['FAILED_LOAD_DATA'];
							this.loading = false;
							console.error('Error loading invoices in doc-appr. loading flag set to false.'); // Log loading state
						}
					});
				} else {
					this.loading = false;
				}
			},
			error: (err) => {
				this.errorMessage = 'Failed to load documents.';
				this.loading = false;
			}
		});
	}

	loadFileClassifications(): void {
		this.loading = true;
		this.docApprovalService.getFilesClassifications().subscribe({
			next: (data: DocumentStatusModel[]) => {
				this.fileClassifications = (data || []).filter(item => Number(item.IsHistory) === this.classMode); // If class mode is history, value will be 1, otherwise 0.
				if (this.fileClassifications.length === 0) {
					this.fileClassifications = [this.getDefaultClassification()];
				}
				this.activeClassificationIndex = 0;
				this.selectedGradeLevel = this.getClassificationGrade(this.fileClassifications[0]);
				this.initData(true);
			},
			error: (err) => {
				console.error('Error fetching file classifications:', err);
				this.fileClassifications = [this.getDefaultClassification()];
				this.activeClassificationIndex = 0;
				this.selectedGradeLevel = 10;
				this.initData(true);
			}
		});
	}

	onClassificationTabChange(event: TabViewChangeEvent): void {
		this.activeClassificationIndex = event.index;
		this.selectedGradeLevel = this.getClassificationGrade(this.fileClassifications[event.index]);
		this.docApprovalStateKey = `${this.getTableStateKeyPrefix()}-${this.selectedGradeLevel}`;
		this.selectedDocuments = [];
		this.expandedRows = {};
		this.cancelBulkMove();
		this.initData(true);
	}

	protected getTableStateKeyPrefix(): string {
		return this.classMode === 1 ? 'tab_doc_history-session' : 'tab_doc_watch-session';
	}

	protected getClassificationGrade(classification?: DocumentStatusModel): number {
		const code = Number(classification?.Code);
		return Number.isFinite(code) ? code : 1;
	}

	protected getDefaultClassification(): DocumentStatusModel {
		return {
			FileGradeClassificationRecID: 0,
			FileGradeClassification: this.translateServ.dictionary['DOC_APP_TITLE'] || 'Documents',
			Grade: '1',
			IconGrade: 'pi pi-bell',
			Code: '10',
			Descr: '',
			IsHistory: this.classMode
		};
	}

	private prepareDocumentForDisplay(doc: Document): Document {
		const dateEntered = this.parseDateEntered(doc.DateEntered);
		const groupDate = dateEntered ? new Date(dateEntered.getFullYear(), dateEntered.getMonth(), 1) : null;
		const monthTextValue = dateEntered?.toLocaleString(undefined, { month: 'long' });
		const translatedMonth = monthTextValue ? this.translateServ.dictionary[monthTextValue] || monthTextValue : null;
		const dayDate = dateEntered ? new Date(dateEntered.getFullYear(), dateEntered.getMonth(), dateEntered.getDate()) : null;

		return {
			...doc,
			DateEnteredMonthYear: dateEntered
				? `${translatedMonth} ${dateEntered.getFullYear()}`
				: this.translateServ.dictionary['UNKNOWN_DATE'] || 'Unknown date',
			DateEnteredGroupSort: groupDate?.getTime() ?? 0,
			DateEnteredDay: dayDate
				? dayDate.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })
				: this.translateServ.dictionary['UNKNOWN_DATE'] || 'Unknown date',
			DateEnteredDaySort: dayDate?.getTime() ?? 0
		};
	}

	private parseDateEntered(value: string | null | undefined): Date | null {
		if (!value) return null;

		const trimmedValue = String(value).trim();
		const ddMmYyyy = /^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})/.exec(trimmedValue);
		if (ddMmYyyy) {
			const day = Number(ddMmYyyy[1]);
			const month = Number(ddMmYyyy[2]) - 1;
			let year = Number(ddMmYyyy[3]);
			if (year < 100) year += year > 50 ? 1900 : 2000;
			const parsedDate = new Date(year, month, day);
			return isNaN(parsedDate.getTime()) ? null : parsedDate;
		}

		const parsedDate = new Date(trimmedValue);
		return isNaN(parsedDate.getTime()) ? null : parsedDate;
	}

	getReassignClassificationOptions(): DocumentStatusModel[] 
	{
		const currentCode = this.fileClassifications[this.activeClassificationIndex]?.Code;
		return this.fileClassifications.filter(classification => classification.Code !== currentCode);
	}

	isExportButtonVisible(): boolean {
		return this.activeClassificationIndex !== 0 || this.selectedGradeLevel === 50;
	}

	getExportButtonLabel(): string {
		if (this.selectedGradeLevel === 50) {
			return this.translateServ.dictionary['RESTORE_TO_DOCS']; // Tab 5 to 1
		}

		if (this.activeClassificationIndex === 2) {
			return this.translateServ.dictionary['EXPORT_ARCHIVE_BTN']; // Tab 2 to 4
		}

		return this.translateServ.dictionary['EXPORT_MOVEIN_BTN']; // Tab 3 to 5
	}

	onExportButtonClick(isAttempt: 0 | 1 = 0): void 
	{
		this.isAttempt = isAttempt;
		if (this.selectedGradeLevel === 50)
		{
			this.moveArchivedToDocuments();
		}
		else if (this.activeClassificationIndex === 2) // Not to export tab, this.selectedGradeLevel = 30
		{
			this.moveSelectedToArchive(this.activeClassificationIndex);
		}
		else if (isAttempt == 0)
		{
			this.askToMoveOthersToTrash();
		}
		else
		{
			this.exportMovein(isAttempt);
		}
	}

	// X/dismiss intentionally does nothing; only accept/reject below act.
	private askToMoveOthersToTrash(): void {
		this.confirmationService.confirm({
			message: this.translateServ.dictionary['MOVE_TO_ARCH_OTHERS_MSG'],
			header: this.translateServ.dictionary['CONFIRM_ACTION'],
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['YES'],
			rejectLabel: this.translateServ.dictionary['NO'],
			acceptIcon: 'pi pi-check',
			rejectIcon: 'pi pi-times',
			accept: () => {
				this.exportThenSplitClassificationTwentyAndArchiveRest();
			},
			reject: (type: ConfirmEventType) => {
				// Only the Reject button should trigger this; ignore the X/close (CANCEL) dismissal.
				if (type === ConfirmEventType.REJECT) {
					this.moveSelectedDocumentsToFortyAndOthersToFifty();
				}
			}
		});
	}

	// No: selected documents go to 40, the rest go to 50.
	private moveSelectedDocumentsToFortyAndOthersToFifty(): void {
		this.exportMovein();
		
		const selectedDocumentIds = new Set(this.selectedDocuments.map(doc => doc.FileRecID));
		const selectedDocuments = this.allDocuments.filter(doc => selectedDocumentIds.has(doc.FileRecID));
		const otherDocuments = this.allDocuments.filter(doc => !selectedDocumentIds.has(doc.FileRecID));

		if (selectedDocuments.length === 0 && otherDocuments.length === 0) {
			return;
		}

		this.loading = true;
		forkJoin([
			...selectedDocuments.map(doc => this.docApprovalService.reassignFilesClassification('40', doc.FileRecID)),
			...otherDocuments.map(doc => this.docApprovalService.reassignFilesClassification('50', doc.FileRecID))
		]).subscribe({
			next: () => {
				this.initData(true);
			},
			error: (err) => {
				console.error('Error moving files to archive:', err);
				this.errorMessage = this.translateServ.dictionary['FAILED_LOAD_DATA'];
				this.loading = false;
			}
		});
	}

	// Yes: downloads the classification 20 selection, applies the same split as No, and independently
	// archives every document left in the other classifications to 50 (not gated on the split's outcome).
	private exportThenSplitClassificationTwentyAndArchiveRest(): void {
		this.exportMovein();
		this.moveSelectedDocumentsToFortyAndOthersToFifty();
		this.archiveAllOtherClassificationsToFifty();
	}

	// Fetches every document sitting in a classification other than the current one and archives it to 50.
	private archiveAllOtherClassificationsToFifty(): void {
		const otherGrades = this.fileClassifications
			.map(classification => this.getClassificationGrade(classification))
			.filter(grade => grade !== this.selectedGradeLevel && grade !== 50);

		if (otherGrades.length === 0) {
			this.initData(true);
			return;
		}

		this.loading = true;
		forkJoin(otherGrades.map(grade => this.docApprovalService.loadAllFiles(grade))).subscribe({
			next: (docsPerGrade) => {
				const otherDocuments = docsPerGrade.flat();

				if (otherDocuments.length === 0) {
					this.initData(true);
					return;
				}

				otherDocuments.forEach(doc => this.workbenchIntegrator.changeStatus(true, doc.FileRecID, true));

				forkJoin(otherDocuments.map(doc => this.docApprovalService.reassignFilesClassification('50', doc.FileRecID))).subscribe({
					next: () => {
						this.syncSelectedDocumentsFromDJWS();
						this.initData(true);
					},
					error: (err) => {
						console.error('Error moving files to archive:', err);
						this.errorMessage = this.translateServ.dictionary['FAILED_LOAD_DATA'];
						this.loading = false;
					}
				});
			},
			error: (err) => {
				console.error('Error loading other classification files:', err);
				this.errorMessage = this.translateServ.dictionary['FAILED_LOAD_DATA'];
				this.loading = false;
			}
		});
	}

	private moveSelectedToArchive(activeTab : number): void {
		const sourceArchiveCode = this.getClassificationGrade(this.fileClassifications[activeTab]);
		const archiveCode = '50';

		if (this.selectedGradeLevel !== sourceArchiveCode || this.allDocuments.length === 0) {
			return;
		}

		this.loading = true;
		forkJoin(this.allDocuments.map(doc => this.docApprovalService.reassignFilesClassification(archiveCode, doc.FileRecID))).subscribe({
			next: () => {
				this.initData(true);
			},
			error: (err) => {
				console.error('Error moving files to archive:', err);
				this.errorMessage = this.translateServ.dictionary['FAILED_LOAD_DATA'];
				this.loading = false;
			}
		});
	}

	private moveArchivedToDocuments(): void {
		const documentsCode = '10';

		if (this.selectedGradeLevel !== 50 || this.allDocuments.length === 0) {
			return;
		}

		this.loading = true;
		forkJoin(this.allDocuments.map(doc => this.docApprovalService.reassignFilesClassification(documentsCode, doc.FileRecID))).subscribe({
			next: () => {
				this.initData(true);
			},
			error: (err) => {
				console.error('Error moving files to documents:', err);
				this.errorMessage = this.translateServ.dictionary['FAILED_LOAD_DATA'];
				this.loading = false;
			}
		});
	}

	reassignFileClassification(classification: DocumentStatusModel, doc: Document, overlayPanel: any): void {
		overlayPanel?.hide?.();
		this.loading = true;

		// If the file is being transforted into "Ready for export" resolve automaticly all the notifications of that file.
		// console.log(`For ${doc.FileRecID}: ${this.selectedGradeLevel} -> ${classification.Code}`); // << DEBUG
		if(this.selectedGradeLevel === 10 && classification.Code == '20') this.resolveAllNotifiactionForFile(doc);
		
		this.docApprovalService.reassignFilesClassification(classification.Code, doc.FileRecID).subscribe({
			next: () => {
				this.initData(true);
			},
			error: (err) => {
				console.error('Error reassigning file classification:', err);
				this.errorMessage = this.translateServ.dictionary['FAILED_LOAD_DATA'];
				this.loading = false;
			}
		});
	}

	// Main part of the group: enters bulk-move mode when idle, or opens the ship overlay once marked.
	// Disabled (via template) while active with nothing marked, so it's never ambiguous with cancel (the "x" part).
	onBulkMoveMainButtonClick(event: Event, overlayPanel: any): void {
		if (!this.bulkMoveModeActive) {
			this.bulkMoveModeActive = true;
			this.bulkMoveFileRecIds = [];
			return;
		}
		if (this.bulkMoveFileRecIds.length > 0) {
			overlayPanel?.toggle?.(event);
		}
	}

	cancelBulkMove(): void {
		this.bulkMoveModeActive = false;
		this.bulkMoveFileRecIds = [];
	}

	// Shortcut: double-clicking the main button while in bulk-move mode marks only the rows currently
	// shown on screen (the active page of the filtered set), not every filtered row across all pages.
	onBulkMoveMainButtonDblClick(dt: any): void {
		if (!this.bulkMoveModeActive) return;
		const first = dt?.first ?? 0;
		const rows = dt?.rows ?? this.displayedDocuments.length;
		const pageDocuments = this.displayedDocuments.slice(first, first + rows);
		this.bulkMoveFileRecIds = pageDocuments.map(doc => doc.FileRecID);
	}

	// While bulk-move mode is active the row truck icon marks/unmarks the row instead of opening its overlay.
	onRowTruckClick(row: Document, overlayPanel: any, event: Event): void {
		if (this.bulkMoveModeActive) {
			this.toggleBulkMarkForRow(row);
			return;
		}
		overlayPanel?.toggle?.(event);
	}

	private toggleBulkMarkForRow(row: Document): void {
		const index = this.bulkMoveFileRecIds.indexOf(row.FileRecID);
		if (index === -1) {
			this.bulkMoveFileRecIds.push(row.FileRecID);
		} else {
			this.bulkMoveFileRecIds.splice(index, 1);
		}
	}

	shipBulkMove(classification: DocumentStatusModel, overlayPanel: any): void {
		overlayPanel?.hide?.();
		const fileRecIds = [...this.bulkMoveFileRecIds];
		if (fileRecIds.length === 0) return;

		this.loading = true;
		forkJoin(fileRecIds.map(id => this.docApprovalService.reassignFilesClassification(classification.Code, id))).subscribe({
			next: () => {
				this.cancelBulkMove();
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: `${fileRecIds.length} ${this.translateServ.dictionary['BULK_MOVE_SUCCESS'] || 'files moved'}`
				});
				this.initData(true);
			},
			error: (err) => {
				console.error('Error bulk reassigning file classification:', err);
				this.errorMessage = this.translateServ.dictionary['FAILED_LOAD_DATA'];
				this.loading = false;
			}
		});
	}

	private resolveAllNotifiactionForFile(doc: Document)
	{
		this.notificationDialogService.resolveAllNotificationsForFile(doc.FileRecID).subscribe({
			next: notifications => {
				const tag = buildNotificationTag(notifications);
				const count = Number(tag.value) || 0;
				this.onNotificationSummaryChange({
					fileRecId: doc.FileRecID,
					NotifCount: count,
					NotifColor: count > 0 ? tag.severity : null,
					tag
				});
			},
			error: error => {
				console.error(`Error resolving notifications for FileRecID ${doc.FileRecID}:`, error);
			}
		});
	}

	// Sync DJWS with PrimeNG selection model
	onSelectionChange(selected: Document[]): void {
		if (this.loading) return;

		// Update DJWS to match the new selection
		// First, uncheck all documents and their journals
		this.allDocuments.forEach(doc => {
			this.workbenchIntegrator.changeStatus(true, doc.FileRecID, false);
			// Uncheck all journals for this document
			if (Array.isArray((doc as any).JournalIds)) {
				(doc as any).JournalIds.forEach((jid: number) => {
					this.workbenchIntegrator.changeStatus(false, jid, false);
				});
			}
		});
		// Then, check all selected documents and their journals
		selected.forEach(doc => {
			this.workbenchIntegrator.changeStatus(true, doc.FileRecID, true);
			if (Array.isArray((doc as any).JournalIds)) {
				(doc as any).JournalIds.forEach((jid: number) => {
					this.workbenchIntegrator.changeStatus(false, jid, true);
				});
			}
		});
		// Sync selectedDocuments from DJWS (in case DJWS logic modifies selection)
		this.syncSelectedDocumentsFromDJWS();
	}
	// Like in WatchComponent: selectedInvoices = all invoices with checked journals in DJWS
	get selectedInvoices(): any[] {
		// Find all checked journals for all documents in DJWS
		const userId = this.viewScopeService.getCurrentCustomer();
		const djwsRaw = localStorage.getItem(this.workbenchIntegrator.getStorageName());
		let djwsObj: any = {};
		try { djwsObj = djwsRaw ? JSON.parse(djwsRaw) : {}; } catch { djwsObj = {}; }
		const userDjws = djwsObj[userId] || {};
		// Flatten all checked journals into an array of { FileRecID, ID }
		const selected: { FileRecID: number, ID: number }[] = [];
		Object.keys(userDjws).forEach(docId => {
			const journals = userDjws[docId];
			if (journals && typeof journals === 'object') {
				Object.keys(journals).forEach(jid => {
					if (journals[jid] === true) {
						selected.push({ FileRecID: parseInt(docId, 10), ID: parseInt(jid, 10) });
					}
				});
			}
		});
		return selected;
	}

	// Sync selectedDocuments with DJWS (and journals with documents)
	syncSelectedDocumentsFromDJWS(): void {
		this.selectedDocuments = this.allDocuments.filter(doc =>
			this.workbenchIntegrator.isDocumentChecked(doc.FileRecID)
		);
		// For each document, if all its journals are checked, ensure document is checked; if none, unchecked; if some, partial
		this.allDocuments.forEach(doc => {
			if (Array.isArray((doc as any).JournalIds) && (doc as any).JournalIds.length > 0) {
				const journalIds = (doc as any).JournalIds;
				const checkedCount = journalIds.filter((jid: number) => this.workbenchIntegrator.isJournalChecked(doc.FileRecID, jid)).length;
				if (checkedCount === journalIds.length) {
					this.workbenchIntegrator.changeStatus(true, doc.FileRecID, true);
				} else if (checkedCount === 0) {
					this.workbenchIntegrator.changeStatus(true, doc.FileRecID, false);
				} // else: partial, leave as is
			}
		});
	}

	// Remove all filters from the table
	clearAllFilters(dt: any, globalSearchInput?: HTMLInputElement): void {
		if (dt?.clear) {
			dt.clear();
		}

		if (dt?.clearState) {
			dt.clearState();
		}

		if (globalSearchInput) {
			globalSearchInput.value = '';
		}

		this.totalSummaryFilter = { total: '', vat: '', vatPercent: '' };
		this.hasActiveTableFilters = false;
		this.displayedDocuments = [...this.allDocuments];
	}

	// Export selected documents (stub)
	exportMovein(isAttempt : 0 | 1 = 0): void {
		const exportStatusCode = 20;
		if (this.selectedGradeLevel !== exportStatusCode) return;

		const exportFileRecIds = new Set(this.allDocuments.map(doc => doc.FileRecID));

		// Debug: log DJWS structure for current user
		const userId = this.viewScopeService.getCurrentCustomer();
		const djwsRaw = localStorage.getItem(this.workbenchIntegrator.getStorageName());
		let djwsObj: any = {};
		try { djwsObj = djwsRaw ? JSON.parse(djwsRaw) : {}; } catch { djwsObj = {}; }
		const userDjws = djwsObj[userId] || {};
		console.log('[exportMovein] DJWS for user', userId, userDjws);
		// Collect all checked journal IDs for this user from DJWS directly
		const allJournalIds: number[] = [];
		const exportedFileRecIds = new Set<number>();
		Object.keys(userDjws).forEach(docId => {
			const fileRecId = parseInt(docId, 10);
			if (!exportFileRecIds.has(fileRecId)) return;

			const journals = userDjws[docId];
			if (journals && typeof journals === 'object') {
				Object.keys(journals).forEach(jid => {
					if (journals[jid] !== true) return;
					const jidNum = parseInt(jid, 10);
					if (this.workbenchIntegrator.isPseudoJournalId(jidNum)) return; // filler journal, nothing real to send to movein
					if (!allJournalIds.includes(jidNum)) allJournalIds.push(jidNum);
					exportedFileRecIds.add(fileRecId);
				});
			}
		});
		console.log('[exportMovein] All checked journal IDs to export:', allJournalIds);

		const journalLessSelected = this.selectedDocuments.filter(doc => exportFileRecIds.has(doc.FileRecID) && this.workbenchIntegrator.isJournalLessDocument(doc.FileRecID));
		if (journalLessSelected.length > 0) {
			this.messageService.add({
				severity: 'warn',
				summary: this.translateServ.dictionary['ATTENTION'] || 'Attention',
				detail: this.translateServ.dictionary['JOURNAL_LESS_EXPORT_WARNING'] || `${journalLessSelected.length} selected file(s) have no journals and were skipped from Movein.`
			});
		}

		if (allJournalIds.length === 0) {
			this.messageService.add({ severity: 'info', summary: 'No Data', detail: 'No journals found for export.' });
			console.warn('[exportMovein] No journals found in DJWS:', userDjws);
			return;
		}
		this.watchService.exportMovein(allJournalIds, this.isAttempt).subscribe({
			next: (res) => {
				console.log("Export success: ", res);

				this.openExportedFile(res?.StatusDescription);
				this.refreshPage();

				// Remove exported journals from workbenchIntegrator
				exportedFileRecIds.forEach(fileRecId => this.workbenchIntegrator.removeDocumentById(fileRecId));
			},
			error: (err) => {
				console.error("Export failed:", err);
			}
		});

		// Remove exported journals from workbenchIntegrator
		this.workbenchIntegrator.removeDocumentsWithNoJournals();
	}

	// Guards against opening server-side file paths (e.g. "D:\..." or "\\server\share\...") as file:// links, which browsers block as a cross-origin security violation.
	private openExportedFile(url: string | undefined | null): void {
		if (!url) {
			console.error("No StatusDescription in response");
			return;
		}
		if (!/^https?:\/\//i.test(url)) {
			console.error("StatusDescription is not a valid URL, refusing to open:", url);
			this.messageService.add({
				severity: 'error',
				summary: this.translateServ.dictionary['ERROR'] || 'Error',
				detail: this.translateServ.dictionary['EXPORT_INVALID_FILE_URL'] || 'The export completed, but the server returned an invalid file link.'
			});
			return;
		}
		window.open(url, '_blank');
	}

	// Handle document checkbox change
	onDocumentCheckboxChange(event: Event, doc: Document): void {
		const checked = (event.target as HTMLInputElement).checked;
		console.log('[doc-approval] Checkbox clicked for doc:', doc.FileRecID, 'checked:', checked);
		if (Array.isArray((doc as any).JournalIds) && (doc as any).JournalIds.length > 0) {
			this.workbenchIntegrator.changeStatus(true, doc.FileRecID, checked);
		} else {
			// fallback: treat docId as single journal
			this.workbenchIntegrator.changeStatus(true, doc.FileRecID, checked);
		}
		this.syncSelectedDocumentsFromDJWS();
		// Refresh app-watch for this document
		if (doc.FileRecID != null && this.watchComponents) {
			const watch = this.watchComponents.find(w => w.SpecificFileRecID === doc.FileRecID);
			if (watch) {
				console.log('[doc-approval] Calling app-watch.loadInvoices() for FileRecID:', doc.FileRecID);
				watch.SpecificFileRecID = doc.FileRecID;
				watch.loadInvoices();
			}
		}
	}

	// Handle header (select all) checkbox change
	onAllDocumentCheckboxChange(event: Event): void {
		const checked = (event.target as HTMLInputElement).checked;
		this.allDocuments.forEach(doc => {
			if (Array.isArray((doc as any).JournalIds) && (doc as any).JournalIds.length > 0) {
				this.workbenchIntegrator.changeStatus(true, doc.FileRecID, checked);
			} else {
				this.workbenchIntegrator.changeStatus(true, doc.FileRecID, checked);
			}
		});
		this.syncSelectedDocumentsFromDJWS();
	}




	/**
	 * Initializes the columnHeadersMap for translated or custom column headers.
	 * This is modeled after the logic in WatchComponent.
	 */
	private initializeColumnHeadersMap(): void {
		const fieldToDictKeyMap: { [key: string]: string } = {
			'FileRecID': 'doc_num',
			'FileCategory': 'JMT_COL_MOVE_TYPE',
			'DateEntered': 'JMT_COL_DATE_REF',
			'CustomerName': 'JMT_COL_CUST_NAME',
			'Business_ID': 'JMT_COL_BUSINESS_ID',
			'total_with_tax': 'JMT_COL_AMOUNT_TOTAL',
			'tax': 'JMT_COL_VAT',
			'vat_in_precentages': 'JMT_COL_VAT_PERCENT',
			'allocation_number': 'JMT_COL_ALLOC_NUMBER',
			'CustomerSupplierExpenseCode': 'JMT_COL_EXPENSE',
			'Sug_tnua': 'JMT_COL_MOVE_TYPE',
			'OriginalFileName': 'JMT_COL_ORIG_FILE_NAME',
			// Add more mappings as needed
		};

		Object.keys(fieldToDictKeyMap).forEach(field => {
			// Fallback to field name if translation service or dictionary is not available
			this.columnHeadersMap[field] = fieldToDictKeyMap[field] || field;
		});
	}


	refreshData(): void {
		this.manualConfirmationData = this.allDocuments.filter(doc => doc.FileCategory !== 'Not to Register' && !doc.isAllowedToSend);
		this.groupedDocuments = this.allDocuments.filter(doc => doc.FileCategory !== 'Not to Register' && doc.isAllowedToSend).sort((a, b) => a.FileCategory.localeCompare(b.FileCategory));
		this.notToRegisterData = this.allDocuments.filter(doc => doc.FileCategory === 'Not to Register');
		// Only reset to "show everything" when no filter is active; otherwise this would silently
		// pull back in rows the table's active filter/search is meant to hide (refreshData() runs
		// after routine per-document actions like onSave/onMoveRecordToTab, not just on load).
		if (!this.hasActiveTableFilters) {
			this.displayedDocuments = [...this.allDocuments];
		}
		// Do NOT overwrite selectedDocuments here; keep it in sync with DJWS only
	}

	onToggleEditMode(doc: Document): void {
		this.editModeId = this.editModeId === doc.FileRecID ? null : doc.FileRecID;
	}

	onSave(doc: Document): void {
		this.messageService.add({ severity: 'success', summary: 'Saved', detail: `Changes for document '${doc.OriginalFileName}' have been saved.` });
		this.onToggleEditMode(doc);
		this.refreshData();
	}

	// New method to handle tab changes and populate move options
	onTabChange(event: TabViewChangeEvent) {
		this.currentTabIndex = event.index;
		this.populateMoveOptions();
	}

	// New method to populate the move dropdown options dynamically
	populateMoveOptions(): void {
		this.moveOptions = this.tabHeaders
			.filter((header, index) => index !== this.currentTabIndex)
			.map(header => ({
				label: header,
				value: header
			}));
	}

	// New consolidated method to handle moving a document to a different tab
	onMoveRecordToTab(doc: Document, targetTab: string): void {
		if (this.tabHeaders[this.currentTabIndex] === targetTab) {
			this.messageService.add({ severity: 'info', summary: 'No Change', detail: 'Document is already in this category.' });
			return;
		}
		this.confirmationService.confirm({
			message: `Are you sure you want to move the document '${doc.OriginalFileName}' to '${targetTab}'?`,
			header: 'Move Document',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				const docIndex = this.allDocuments.findIndex(d => d.FileRecID === doc.FileRecID);
				if (docIndex !== -1) {
					switch (targetTab) {
						case 'Manual Confirmation':
							this.allDocuments[docIndex].isAllowedToSend = false;
							if (this.allDocuments[docIndex].FileCategory === 'Not to Register') {
								this.allDocuments[docIndex].FileCategory = 'Travel';
							}
							break;
						case 'Auto Confirmed':
							this.allDocuments[docIndex].isAllowedToSend = true;
							break;
						case 'Not to Register':
							this.allDocuments[docIndex].isAllowedToSend = false;
							this.allDocuments[docIndex].FileCategory = 'Not to Register';
							break;
					}
				}
				this.refreshData();
				this.messageService.add({ severity: 'success', summary: 'Moved', detail: `Document '${doc.OriginalFileName}' has been moved to '${targetTab}'.` });
			}
		});
	}

	// Old methods (kept for reference, but new button logic uses onMoveRecordToTab)
	onDecline(doc: Document): void {
		this.onMoveRecordToTab(doc, 'Not to Register');
	}

	onConfirm(doc: Document): void {
		this.onMoveRecordToTab(doc, 'Auto Confirmed');
	}

	onMoveRecord(doc: Document, newExpenseType: string): void {
		if (doc.FileCategory === newExpenseType) {
			this.messageService.add({ severity: 'info', summary: 'No Change', detail: 'Document is already in this expense type.' });
			return;
		}
		this.confirmationService.confirm({
			message: `Are you sure you want to move the document '${doc.OriginalFileName}' to '${newExpenseType}'?`,
			header: 'Manual Confirmation',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				const docIndex = this.allDocuments.findIndex(d => d.FileRecID === doc.FileRecID);
				if (docIndex !== -1) {
					this.allDocuments[docIndex].FileCategory = newExpenseType;
					if (doc.FileCategory === 'Not to Register' && newExpenseType !== 'Not to Register') {
						this.allDocuments[docIndex].isAllowedToSend = false;
					} else {
						this.allDocuments[docIndex].isAllowedToSend = true;
					}
				}
				this.refreshData();
				this.messageService.add({ severity: 'success', summary: 'Confirmed', detail: `Document '${doc.OriginalFileName}' has been moved to '${newExpenseType}'.` });
			}
		});
	}

	onReturnToRegister(doc: Document, newExpenseType: string): void {
		this.onMoveRecordToTab(doc, 'Manual Confirmation');
	}

	onMarkAsTrash(doc: Document): void {
		this.confirmationService.confirm({
			message: `Are you sure you want to permanently delete the document '${doc.OriginalFileName}'? This action cannot be undone.`,
			header: 'Warning',
			icon: 'pi pi-trash',
			accept: () => {
				this.allDocuments = this.allDocuments.filter(d => d.FileRecID !== doc.FileRecID);
				this.refreshData();
				this.messageService.add({ severity: 'warn', summary: 'Deleted', detail: `Document '${doc.OriginalFileName}' has been marked as trash and deleted.` });
			}
		});
	}



	// Called when table is sorted or filtered
	onTableSortOrFilter(event: any): void {
		// event.filteredValue is the current filtered data
		this.displayedDocuments = event && event.filteredValue ? event.filteredValue : [...this.allDocuments];
		this.hasActiveTableFilters = this.hasTableFilters(event?.filters);
	}

	private hasTableFilters(filters: any): boolean {
		if (!filters) return false;

		return Object.values(filters).some((filter: any) => {
			const values = Array.isArray(filter) ? filter : filter?.constraints || [filter];
			return values.some((constraint: any) => {
				const value = constraint?.value ?? constraint;
				return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
			});
		});
	}

	// Smart filter for Totals Summary column
	onTotalsSummaryFilter(filterCallback: any): void {
		filterCallback({
			value: { ...this.totalSummaryFilter },
			matchMode: 'custom',
			operator: 'and'
		});
	}

	// p-columnFilter only seeds dt.filters[field] on its own init, so switching the sort/filter field
	// via the dropdown inside the filter menu needs this to avoid showing an empty constraint row.
	ensureFilterConstraint(dt: any, field: string, type: 'numeric' | 'text'): void {
		if (!dt?.filters || dt.filters[field]) return;
		dt.filters[field] = [{ value: null, matchMode: type === 'numeric' ? 'equals' : 'startsWith', operator: 'and' }];
	}

	getColumnHeader(field: string): string {
		return this.columnHeadersMap[field] || field;
	}

	openPreview(fileRecId: number): void {
		console.log('Opening preview for invoice:', fileRecId);
		const doc = this.allDocuments.find(d => d.FileRecID === fileRecId);
		if (!doc) return;
		// Prepare data to pass to EyeDialogComponent
		const data =
		{
			invoice: doc,
			displayedInvoices: this.displayedDocuments,
			currentDocumentIndex: this.displayedDocuments.findIndex(inv => inv.FileRecID === fileRecId),
			currentClassification: this.selectedGradeLevel,
			direction: this.direction,
			columnHeadersMap: this.columnHeadersMap,
			parentComp: this,
			isEditAllowed: this.classMode === 0
		};
		this.ref = this.dialogService.open(EyeDialogComponent, {
			header: this.translateServ.dictionary['JOURNAL_MOVES_PREVIEW_TITLE'] || 'Preview',
			width: '70vw',
			height: '80vw',
			data,
			modal: true,
			dismissableMask: true,
			draggable: true, 		// allow moving the dialog
			closable: true,        // Show close button in header
			maximizable: true,     // Show maximize button in header
			minimizable: true,     // Show minimize button in header (if supported by your PrimeNG version)
			imports: [
				CommonModule,
				DialogModule,
				TabViewModule,
				TableModule,
				TagModule,
				TooltipModule,
				AutoComplete,
				ButtonModule,
				ProgressBarModule,
				FormsModule,
				EyeDialogComponent
			]
		} as any);
		this.ref.onClose.subscribe(() => {
			// Check the service instead of the emitted 'changesMade' variable
			if (this.docApprovalService.dialogChangesMade) {
				this.reloadData();
			}
		})
	}

	// Returns true if the row is the last viewed document (for row highlight)
	isLastlyViewed(doc: Document): boolean 
	{
		const lastViewed = this.workbenchIntegrator.getLastViewed() as LastViewed;
		return lastViewed && typeof lastViewed.documentId === 'number' && doc.FileRecID === lastViewed.documentId;
	}

	reloadData() {
		this.initData(true); // Calling for the function that will refill the tables with fresh data.
		this.docApprovalService.dialogChangesMade = false; // IMPORTANT: Reset the flag for the next time the dialog opens
	}

	toggleRowExpanded(row: Document): void {
		row.expanded = !row.expanded;
		if (row.expanded) {
			this.loadGradesInformation(row.FileRecID);
		}
	}

	loadGradesInformation(fileRecId: number): void {
		if (this.fileGradesLoading[fileRecId] || this.fileGroupGrades[fileRecId] || this.fileFeatureGrades[fileRecId]) {
			return;
		}

		this.fileGradesLoading[fileRecId] = true;
		forkJoin({
			groups: this.docApprovalService.getGroupsGradesForFile(fileRecId),
			features: this.docApprovalService.getFeaturesGradesForFile(fileRecId)
		}).subscribe({
			next: ({ groups, features }) => {
				const groupColorMap = this.buildGroupGradeColorMap(groups);
				this.fileGroupGrades[fileRecId] = groups;
				this.fileFeatureGrades[fileRecId] = this.applyGroupColorsToFeatures(features, groupColorMap);
				this.fileGradePolarAreaData[fileRecId] = this.buildGradePolarAreaData(groups);
				this.fileGradesLoading[fileRecId] = false;
			},
			error: (err) => {
				console.error('Error loading grades information:', err);
				this.fileGroupGrades[fileRecId] = [];
				this.fileFeatureGrades[fileRecId] = [];
				this.fileGradePolarAreaData[fileRecId] = this.buildGradePolarAreaData([]);
				this.fileGradesLoading[fileRecId] = false;
			}
		});
	}

	private applyGroupColorsToFeatures(features: FileFeatureGradeModel[], groupColorMap: Map<string, string>): FileFeatureGradeViewModel[] {
		return features.map(feature => ({
			...feature,
			ChartColorNotification: groupColorMap.get(this.getNotificationTypeCodeKey(feature.NotificationTypeCode))
		}));
	}

	private buildGroupGradeColorMap(groups: DocumentGroupsGradesModel[]): Map<string, string> {
		const documentStyle = getComputedStyle(document.documentElement);
		const chartColors = this.getGradeChartColors(documentStyle);
		const groupColorMap = new Map<string, string>();

		groups.forEach((group, index) => {
			const notificationTypeCode = this.getNotificationTypeCodeKey(group.NotificationTypeCode);
			if (!groupColorMap.has(notificationTypeCode)) {
				groupColorMap.set(notificationTypeCode, chartColors[index % chartColors.length]);
			}
		});

		return groupColorMap;
	}

	private buildGradePolarAreaData(groups: DocumentGroupsGradesModel[]): any {
		const documentStyle = getComputedStyle(document.documentElement);
		this.applyGradeChartThemeColors(documentStyle);
		const chartColors = this.getGradeChartColors(documentStyle);
		const groupColorMap = this.buildGroupGradeColorMap(groups);

		return {
			labels: groups.map(group => group.FileCategory),
			datasets: [
				{
					label: this.translateServ.dictionary['GRADE'] || 'Grade',
					data: groups.map(group => this.normalizeGrade(group.NotificationGradeGrade)),
					backgroundColor: groups.map((group, index) => groupColorMap.get(this.getNotificationTypeCodeKey(group.NotificationTypeCode)) || chartColors[index % chartColors.length])
				}
			]
		};
	}

	private getNotificationTypeCodeKey(notificationTypeCode: string | number | null | undefined): string {
		return String(notificationTypeCode ?? '').trim();
	}

	private getGradeChartColors(documentStyle: CSSStyleDeclaration): string[] {
		return [
			documentStyle.getPropertyValue('--p-blue-500').trim() || '#3B82F6',
			documentStyle.getPropertyValue('--p-green-500').trim() || '#22C55E',
			documentStyle.getPropertyValue('--p-yellow-500').trim() || '#EAB308',
			documentStyle.getPropertyValue('--p-orange-500').trim() || '#F97316',
			documentStyle.getPropertyValue('--p-purple-500').trim() || '#A855F7',
			documentStyle.getPropertyValue('--p-red-500').trim() || '#EF4444',
		];
	}

	private applyGradeChartThemeColors(documentStyle = getComputedStyle(document.documentElement)): void {
		this.fileGradePolarAreaOptions = {
			...this.fileGradePolarAreaOptions,
			plugins: {
				...this.fileGradePolarAreaOptions.plugins,
				legend: {
					...this.fileGradePolarAreaOptions.plugins.legend,
					labels: {
						...this.fileGradePolarAreaOptions.plugins.legend.labels,
						color: documentStyle.getPropertyValue('--text-color') || '#111827'
					}
				}
			},
			scales: {
				...this.fileGradePolarAreaOptions.scales,
				r: {
					...this.fileGradePolarAreaOptions.scales.r,
					grid: {
						...this.fileGradePolarAreaOptions.scales.r.grid,
						color: documentStyle.getPropertyValue('--surface-border') || '#E5E7EB'
					},
					ticks: {
						...this.fileGradePolarAreaOptions.scales.r.ticks,
						color: documentStyle.getPropertyValue('--text-color-secondary') || '#6B7280'
					}
				}
			}
		};
	}

	private normalizeGrade(value: number | string | null | undefined): number {
		const grade = Number(value);
		if (!Number.isFinite(grade)) return 1;
		return Math.min(5, Math.max(1, grade));
	}

	// Method to handle row expansion
	onRowExpand(event: any): void {
		const rowData = event.data;
		if (rowData && rowData.FileRecID) {
			this.expandedRows[rowData.FileRecID] = true;
		}
	}

	// Method to handle row collapse
	onRowCollapse(event: any): void {
		const rowData = event.data;
		if (rowData && rowData.FileRecID) {
			delete this.expandedRows[rowData.FileRecID];
		}
	}

	// Helper for template: are all documents checked?
	isAllDocumentsChecked(): boolean {
		return this.allDocuments.length > 0 && this.allDocuments.every(doc => this.workbenchIntegrator.isDocumentChecked(doc.FileRecID));
	}

	// Helper for template: is header checkbox indeterminate?
	isSomeDocumentsIndeterminate(): boolean {
		return this.allDocuments.some(doc => this.workbenchIntegrator.isDocumentPartiallySelected(doc.FileRecID)) && !this.isAllDocumentsChecked();
	}

	// Returns 'V' if all journals selected, '/' if partial, '' if none
	getJournalSelectionIcon(doc: Document): string {
		// All journals selected
		if (this.workbenchIntegrator.isDocumentChecked(doc.FileRecID)) {
			return '✔'; // V
		}
		// Some journals selected
		if (this.workbenchIntegrator.isDocumentPartiallySelected(doc.FileRecID)) {
			return '/';
		}
		// None selected
		return '';
	}

	// Checkbox state: checked if all journals, indeterminate if some, unchecked if none
	isDocumentCheckboxChecked(doc: Document): boolean {
		return this.workbenchIntegrator.isDocumentChecked(doc.FileRecID);
	}

	// Called when DJWS changes from a child (e.g., app-watch)
	onDJWSChanged(isParentRequest : boolean , changedFileRecID?: number, changedJournalRecID?: number): void 
	{
		if (changedJournalRecID != null && changedFileRecID != null && this.watchComponents) {
			const currectStatus = this.workbenchIntegrator.isJournalChecked(changedFileRecID, changedJournalRecID);
			this.workbenchIntegrator.changeStatus(false, changedFileRecID, currectStatus);
			const watch = this.watchComponents.find(w => w.SpecificFileRecID === changedFileRecID);
			if (watch) {
				watch.onDJWSChanged(true, changedFileRecID, changedJournalRecID);
			}
		}
		this.syncSelectedDocumentsFromDJWS();
	}


	private refreshPage(): void {
		this.refreshService.triggerPageRefresh();
	}


	// Computed property: get file info for dialog
	get workbenchList() {
		return this.workbenchFileRecIds
			.map(id => this.allDocuments.find(doc => doc.FileRecID === id))
			.filter(doc => !!doc) as Document[];
	}

	// Toggle FileRecID in workbench
	addToWorkbench(fileRecId: number) {
		const idx = this.workbenchFileRecIds.indexOf(fileRecId);
		if (idx === -1) {
			this.workbenchFileRecIds.push(fileRecId);
			this.messageService.add({
				severity: 'info',
				summary: this.translateServ.dictionary["WORKBENCH_MERGE"],
				detail: `${this.translateServ.dictionary["FILE"]} ${fileRecId} ${this.translateServ.dictionary["WORKBENCH_ADDED_MSG"]}`
			});
		} else {
			this.workbenchFileRecIds.splice(idx, 1);
			this.messageService.add({
				severity: 'info',
				summary: this.translateServ.dictionary["WORKBENCH_MERGE"],
				detail: `${this.translateServ.dictionary["FILE"]} ${fileRecId} ${this.translateServ.dictionary["WORKBENCH_REMOVED_MSG"]}`
			});
		}
	}

	// Remove FileRecID from workbench
	removeFromWorkbench(fileRecId: number) {
		this.workbenchFileRecIds = this.workbenchFileRecIds.filter(id => id !== fileRecId);
	}

	// Open the workbench dialog
	openWorkbenchDialog() {
		this.displayWorkbenchDialog = true;
	}

	get journalLessDocuments(): Document[] {
		return this.allDocuments.filter(doc => this.workbenchIntegrator.isJournalLessDocument(doc.FileRecID));
	}

	get hasJournalLessSelection(): boolean {
		return this.selectedDocuments.some(doc => this.workbenchIntegrator.isJournalLessDocument(doc.FileRecID));
	}

	openJournalLessDialog(): void {
		this.displayJournalLessDialog = true;
	}

	// Merge action: clear workbench after merge
	mergeWorkbench() {
		this.messageService.add({ severity: 'success',life: 5000, summary: this.translateServ.dictionary["MERGE"], detail: this.translateServ.dictionary['MERGE_TRIGGERED_MSG'] });
		this.workbenchFileRecIds = [];
	}

	// Called when merge dialog is closed, with info if merge was performed
	onMergeDialogClosed(event: { merged: boolean }) {
		if (event && event.merged) {
			this.reloadData();
		}
	}

	public getClassMode()
	{
		return this.classMode;
	}

	getBadgeSeverity(color: string | null): 'info' | 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' {
		if (!color) {
			return 'success';
		}
		if (typeof color !== 'string') {
			return 'secondary';
		}
		return color as any;
	}

	getNotificationTag(row: { NotifCount?: number | string | null; NotifColor?: string | null }): NotificationTagInfo {
		return buildNotificationTag(row);
	}

	private applyUnresolvedNotificationSummaries(rows: Document[]): void {
		const fileRecIds = [...new Set(rows.map(row => row.FileRecID).filter((fileRecId): fileRecId is number => typeof fileRecId === 'number'))];
		if (fileRecIds.length === 0) return;

		forkJoin(fileRecIds.map(fileRecId =>
			this.notificationDialogService.getFileNotifications(fileRecId).pipe(
				map(notifications => ({ fileRecId, tag: buildNotificationTag(notifications) }))
			)
		)).subscribe(summaries => {
			summaries.forEach(summary => {
				const count = Number(summary.tag.value) || 0;
				this.onNotificationSummaryChange({
					fileRecId: summary.fileRecId,
					NotifCount: count,
					NotifColor: count > 0 ? summary.tag.severity : null,
					tag: summary.tag
				});
			});
		});
	}

	// Notification dialog state for DocApproval
	displayNotificationsDialog: boolean = false;
	currentFileRecIdForNotification: number | null = null;

	openNotificationsDialog(doc: Document): void {
		this.currentFileRecIdForNotification = doc.FileRecID;
		this.displayNotificationsDialog = true;
	}

	onNotificationSummaryChange(summary: NotificationSummaryChange): void {
		this.allDocuments
			.filter(doc => doc.FileRecID === summary.fileRecId)
			.forEach(doc => {
				(doc as any).NotifCount = summary.NotifCount;
				(doc as any).NotifColor = summary.NotifColor;
			});

		this.watchComponents?.forEach(watch => watch.applyNotificationSummaryChange(summary));
	}

	/**
	 * Scrolls the table to the last viewed document and highlights/selects it.
	 */
	goToLastViewed(dt: any): void {
		if (!dt) return;
		const lastViewed = this.workbenchIntegrator.getLastViewed() as LastViewed;
		if (!lastViewed || typeof lastViewed.documentId !== 'number') return;

		// Find the index of the last viewed document in the current allDocuments
		const index = this.allDocuments.findIndex(i => i.FileRecID === lastViewed.documentId);
		if (index === -1) return;

		// Calculate the page number (PrimeNG Table is 0-based)
		const rowsPerPage = dt.rows || 10;
		const page = Math.floor(index / rowsPerPage);
		dt.first = page * rowsPerPage;

		setTimeout(() => {
			// const lastViewedDoc = this.allDocuments[index];
			// if (lastViewedDoc && !this.selectedDocuments.some(doc => doc.FileRecID === lastViewedDoc.FileRecID)) {
			// 	this.selectedDocuments = [lastViewedDoc];
			// }
			// Try to scroll to the row in the DOM (if needed)
			const rowElem = document.querySelector(`#tab_doc_watch tr.lastly-viewed-row`);
			if (rowElem && typeof (rowElem as any).scrollIntoView === 'function') {
				(rowElem as any).scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}, 200);
	}

	// Called when merge dialog visibility changes
	onMergeDialogVisibleChange(visible: boolean) 
	{
		// No-op: logic moved to onMergeDialogClosed for more control
	}

	
}